import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import DataTable, { CellPrimary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import YearSelect from 'components/FormFields/YearSelect';
import PageContainer from 'components/PageContainer';
import { HeaderContainer, HeaderTitle } from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import SearchInput from 'components/SearchInput';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { useGetTrackRegistrations } from './gql/get-track-registrations';

export const TrackRegistrationsEventsDesktop = () => {
  const [events, setEvents] = useState([]);
  const [count, setCount] = useState(0);

  const { pathname } = useLocation();
  const userType = pathname.split('/').includes('admin-employee')
    ? 'employee'
    : 'track';
  const routePrefix = `/admin-${userType}`;

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Year filter - use URL param directly (no local state needed)
  const thisYear = Number(moment().format('YYYY'));
  const yearFilter = input.year ? Number(input.year) : thisYear;

  const handleYearChange = (e) => {
    const newYear = Number(e.target.value);
    updateQuery({ year: newYear, page: 1 });
  };

  // Fetch events data
  const { data, loading } = useGetTrackRegistrations();

  useEffect(() => {
    if (data?.getEventsByAccount) {
      setEvents(data.getEventsByAccount.results || []);
      setCount(data.getEventsByAccount.count || 0);
    }
  }, [data]);

  // Handle search - only reset page when user actively searches, not on mount
  const handleSearch = useCallback(
    (searchTerm) => {
      // Only update if the search term actually changed
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // DataTable columns
  const columns = [
    {
      field: 'fullDate',
      header: 'Date',
      render: (event) => {
        const { isMultiDay, day, date, month, listDates, year } = event;
        return isMultiDay
          ? `${month?.toUpperCase()} ${listDates} - ${year}`
          : `${day} ${date} - ${year}`;
      },
    },
    {
      field: 'name',
      header: 'Event',
      render: (item) => <CellPrimary>{item.name}</CellPrimary>,
    },
    {
      field: 'track',
      header: 'Tracks',
      render: (event) => event.track?.name || '',
    },
    {
      field: 'status',
      header: 'Status',
    },
  ];

  const totalPages = Math.ceil(count / 15);

  // Actions for DataTable
  const actions = [
    {
      type: 'view',
      title: 'View Event',
      mobileLabel: 'View Event',
      to: (item) => `${routePrefix}/registrations/event/${item.id}`,
    },
  ];

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="events" />
            <HeaderTitle>Registrations</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Content Card */}
        <Card noPadding>
          <ContentWrapper>
            <PageToolbar>
              <SearchInput
                onSearch={handleSearch}
                placeholder="Search events..."
              />
              <YearSelect
                value={yearFilter}
                onChange={handleYearChange}
              />
            </PageToolbar>

            <DataTable
              columns={columns}
              data={events}
              loading={loading}
              keyField="id"
              emptyMessage="No events found"
              actions={actions}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </ContentWrapper>
        </Card>
      </PageContainer>
    </EnhancedThemeProvider>
  );
};
